import React, { useCallback, useState, useMemo, useEffect } from "react"
import { AppDetailBase } from "../AppDetailBase/AppDetailBase"
import { CollaboratorTable } from "../../../Common/CollaboratorTable/CollaboratorTable"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import {
    setCollaboratorStatus,
    sendInvitations,
    loadMoreCollaborators,
    setCollaboratorRole,
    deleteCollaborator
} from "../../../../../../Actions/API/Collaborator/CollaboratorActions"
import {
    CollaboratorStatus,
    APICollaborationModel,
    APICollaborationParams,
    CollaboratorRole
} from "../../../../../../Models/API/CollaboratorModel"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../Models/Client/ProjectRole"
import {
    InviteSubmitFields,
    CollaboratorInviteModal
} from "../../../Common/CollaboratorTable/CollaboratorInviteModal"
import { Loader, Button } from "dash-styled"
import { useCollaborators } from "../../../../../../Hooks/useCollaborators"
import {
    hasValue,
    FetchState,
    getValue
} from "../../../../../../Models/Client/FetchState"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { CollaboratorRoleModal } from "../../../Common/CollaboratorTable/CollaboratorRoleModal"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import {
    getEmail,
    selectHasFreemiumExpired
} from "../../../../../../Helpers/Functions/Selectors/auth"
import { useCollaboratorCopyEmails } from "../../../../../../Hooks/useCollaboratorCopyEmails"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import {
    APIAppInstanceModel,
    isAppInstanceEnabled
} from "../../../../../../Models/API/AppInstanceModel"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import { useBoolean } from "../../../../../../Hooks/useBoolean"

type Modal =
    | { type: "invite" }
    | { type: "changeRole"; data: ChangeRole }
    | { type: "delete"; user: APICollaborationModel; isProcessing: boolean }

interface ProjectCollaboratorsProps {
    orgUsername: string
    appID: string
    invite?: boolean
    params: APICollaborationParams
    filterAndSort: FilterAndSortExpression<APICollaborationModel>
    modal?: Modal
}

interface StateProps {
    items: APICollaborationModel[] | undefined
    total: number
    myEmail: string
    rolesFetch: FetchState<ProjectRole[]>
    instanceFetch: FetchState<APIAppInstanceModel>
    isCopyProcessing: boolean
}

export interface ChangeRole {
    id: number
    email: string
    role: CollaboratorRole
}

interface DispatchProps {
    viewPermissions: () => void
    toggleActive?: (id: number, status: CollaboratorStatus) => Promise<void>
    onSendInvite: (fields: InviteSubmitFields) => Promise<void>
    isAppEnabled?: boolean
    onLoadMore: () => Promise<void>
    onSort: (sort: SortExpression<APICollaborationModel>) => void
    onChangeRole?: (args: ChangeRole) => void
    onSubmitChangeRole?: (role: CollaboratorRole) => Promise<void>
    onInvite?: () => void
    onCancelInvite: () => void
    onCopyEmails?: () => void
    onDelete?: (id: number) => void
    onConfirmDelete: () => void
    onCancel: () => void
}

type Props = ProjectCollaboratorsProps & StateProps & DispatchProps

export const ProjectCollaboratorsIsolated: React.FC<Props> = ({
    orgUsername,
    appID,
    myEmail,
    rolesFetch,
    viewPermissions,
    modal,
    toggleActive,
    onSendInvite,
    items,
    total,
    onLoadMore,
    filterAndSort,
    isCopyProcessing,
    isAppEnabled,
    onSort,
    onChangeRole,
    onSubmitChangeRole,
    onInvite,
    onCancelInvite,
    onCopyEmails,
    onDelete,
    onConfirmDelete,
    onCancel
}: Props) => {
    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

    const handleInvite = useCallback(
        () => (hasFreemiumPlanExpired ? showUserExpiryContact() : onInvite?.()),
        [hasFreemiumPlanExpired, onInvite, showUserExpiryContact]
    )

    const render = () => {
        if (!hasValue(rolesFetch)) {
            return <Loader />
        }

        return (
            <>
                {isAppEnabled === true && (
                    <>
                        {onInvite === undefined ? null : (
                            <Button onClick={handleInvite} className="w-48">
                                Invite people
                            </Button>
                        )}
                    </>
                )}
                <CollaboratorTable
                    myEmail={myEmail}
                    roles={rolesFetch.value}
                    items={items}
                    loadMore={onLoadMore}
                    total={total}
                    toggleActive={toggleActive}
                    viewPermissions={viewPermissions}
                    sort={filterAndSort.sort}
                    isCopyProcessing={isCopyProcessing}
                    onChangeSort={onSort}
                    onAdd={isAppEnabled ? handleInvite : undefined}
                    onChangeRole={onChangeRole}
                    onCopyEmails={onCopyEmails}
                    onDelete={onDelete}
                />
            </>
        )
    }

    const showModal = () => {
        switch (modal?.type) {
            case "invite":
                return (
                    <CollaboratorInviteModal
                        onCancel={onCancelInvite}
                        onSendInvite={onSendInvite}
                        viewPermissions={viewPermissions}
                    />
                )
            case "changeRole":
                return (
                    <CollaboratorRoleModal
                        email={modal.data.email}
                        currentRole={modal.data.role}
                        onSubmit={onSubmitChangeRole}
                        toggleModal={onCancel}
                        onViewPermissions={viewPermissions}
                    />
                )
            case "delete":
                return (
                    <Confirm
                        title={`Remove ${modal.user.collaboratorEmail} from project?`}
                        isProcessing={modal.isProcessing}
                        yesLabel={modal.isProcessing ? "Deleting..." : "Delete"}
                        noLabel="Cancel"
                        onClose={onCancel}
                        onClickNo={onCancel}
                        onClickYes={onConfirmDelete}
                    />
                )
        }
    }

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="Collaborators"
            breadcrumbs={[]}
            fixedContentWidth={false}
            render={render}
            renderModalView={showModal}
            forceUserExpiryContact={userExpiryContact}
            resetUserExpiryContact={hideUserExpiryContact}
        />
    )
}

export const ProjectCollaborators: React.FC<ProjectCollaboratorsProps> = ({
    orgUsername,
    appID,
    params,
    filterAndSort,
    invite = false
}: ProjectCollaboratorsProps) => {
    const dispatch: MainThunkDispatch = useDispatch<MainThunkDispatch>()
    const myEmail = useAppState(getEmail)
    const rolesFetch = useAssumedRoles(appID)
    const collaboratorsFetch = useCollaborators(appID, params)
    const { items, total } = hasValue(collaboratorsFetch)
        ? collaboratorsFetch.value
        : { items: [], total: 0 }
    const instanceFetch = useAppInstance(appID)
    const isAppEnabled = isAppInstanceEnabled(getValue(instanceFetch))
    /**
     * When role modal has data, it displays the modal
     */
    const [modal, setModal] = useState<Modal | undefined>(
        invite ? { type: "invite" } : undefined
    )

    const handleViewPermission = () =>
        window.open(
            routePaths.docs("user-permissions"),
            "_blank",
            "noopener,noreferrer"
        )

    const handleToggleActive = useMemo(() => {
        if (
            !hasAppPermission(
                getValue(rolesFetch) ?? [],
                "collaborators:update"
            )
        ) {
            return undefined
        }

        return async (id: number, status: CollaboratorStatus) =>
            dispatch(setCollaboratorStatus(appID, id, status))
    }, [dispatch, appID, rolesFetch])

    const handleSendInvite = useCallback(
        async ({ emails, role }: InviteSubmitFields) => {
            await dispatch(sendInvitations(appID, emails, role))
            dispatch(
                push(
                    routePaths.appCollaborators(
                        orgUsername,
                        appID,
                        filterAndSort
                    )
                )
            )
        },
        [dispatch, appID, orgUsername, filterAndSort]
    )

    const handleCancelInvite = useCallback(
        () =>
            dispatch(
                push(
                    routePaths.appCollaborators(
                        orgUsername,
                        appID,
                        filterAndSort
                    )
                )
            ),
        [dispatch, orgUsername, appID, filterAndSort]
    )

    const loadMore = () => dispatch(loadMoreCollaborators(appID))

    const handleChangeSort = (s: SortExpression<APICollaborationModel>) => {
        dispatch(
            push(
                routePaths.appCollaborators(
                    orgUsername,
                    appID,
                    { ...filterAndSort, sort: s },
                    invite ? "invite" : "list"
                )
            )
        )
    }

    const handleSubmitChangeRole = useCallback(
        async (role: CollaboratorRole) => {
            if (modal?.type === "changeRole") {
                await dispatch(setCollaboratorRole(appID, modal.data.id, role))
            }
            setModal(undefined)
        },
        [modal, dispatch, appID]
    )
    const handleChangeRole = useCallback(
        (data: ChangeRole) => setModal({ type: "changeRole", data }),
        []
    )
    const handleInvite = useMemo(() => {
        if (
            !hasAppPermission(
                getValue(rolesFetch) ?? [],
                "collaborators:invite"
            )
        ) {
            return undefined
        }

        const path = routePaths.appCollaborators(
            orgUsername,
            appID,
            filterAndSort,
            "invite"
        )

        return () => dispatch(push(path))
    }, [rolesFetch, orgUsername, appID, filterAndSort, dispatch])
    const handleDelete = useCallback(
        (id: number) => {
            const user = getValue(collaboratorsFetch)?.items.find(
                i => i.id === id
            )

            if (user === undefined) {
                return
            }

            setModal({ type: "delete", user, isProcessing: false })
        },
        [collaboratorsFetch]
    )
    const handleConfirmDelete = useCallback(async () => {
        if (modal?.type === "delete") {
            setModal({ ...modal, isProcessing: true })
            dispatch(deleteCollaborator(appID, modal.user.id))
                .then(() => setModal(undefined))
                .catch(() => setModal({ ...modal, isProcessing: false }))
        }
    }, [modal, dispatch, appID])
    const handleCancel = useCallback(() => setModal(undefined), [])

    const copy = useCollaboratorCopyEmails(appID, params)

    useEffect(() => {
        setModal(invite ? { type: "invite" } : undefined)
    }, [invite])

    return (
        <ProjectCollaboratorsIsolated
            orgUsername={orgUsername}
            appID={appID}
            myEmail={myEmail || ""}
            rolesFetch={rolesFetch}
            items={
                hasValue(collaboratorsFetch) || !isAppEnabled
                    ? items
                    : undefined
            }
            total={total}
            viewPermissions={handleViewPermission}
            toggleActive={handleToggleActive}
            onSendInvite={handleSendInvite}
            params={params}
            onLoadMore={loadMore}
            filterAndSort={filterAndSort}
            isCopyProcessing={copy.isProcessing}
            isAppEnabled={isAppEnabled}
            modal={modal}
            instanceFetch={instanceFetch}
            onSort={handleChangeSort}
            onChangeRole={handleChangeRole}
            onSubmitChangeRole={handleSubmitChangeRole}
            onInvite={handleInvite}
            onDelete={handleDelete}
            onCancelInvite={handleCancelInvite}
            onCopyEmails={copy.perform}
            onConfirmDelete={handleConfirmDelete}
            onCancel={handleCancel}
        />
    )
}
